import { ApolloError } from '@apollo/client'
import { Features } from 'generated/featureDefinitions'
import { TaxAgencyHomologationHookDocument } from 'generated/resources/graphql'

// ExpensesFile is used as a type annotation but not directly imported from a query
// It's typically used in fragments, so we'll keep it as a generic type
// If needed, it can be extracted from GetExpensablesQuery or similar queries that return files
type ExpensesFile = {
  id: number
  byteSize?: number | null
  contentType?: string | null
  filename?: string | null
  url?: string | null
}

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { Expensable } from 'modules/expenses/types/Expensable'

import { LegalEntity } from './useLegalEntities'

export enum SignableOrganizationPerCountry {
  es = 'aeat',
  fr = 'urssaf',
  it = 'ade',
}

export const taxAgencyOrgsTexts = (organizationId: SignableOrganizationPerCountry) => {
  const copies = {
    aeat: {
      termsTitle: i18n.t('expenses.settings.signed_invoices.aeat.terms_title'),
      termsDescription: i18n.t('expenses.settings.signed_invoices.aeat.terms_description'),
      termsLink: 'https://factorialhr.es/terminos-y-condiciones-expenses-aeat',
      termsModalTitle: i18n.t('expenses.settings.signed_invoices.terms.aeat_modal_title'),
      termsModalDescription: i18n.t(
        'expenses.settings.signed_invoices.terms.aeat_modal_description'
      ),
      documentsDescription: i18n.t('expenses.settings.signed_invoices.aeat.documents_description'),
    },
    urssaf: {
      termsTitle: i18n.t('expenses.settings.signed_invoices.urssaf.terms_title'),
      termsDescription: i18n.t('expenses.settings.signed_invoices.urssaf.terms_description'),
      termsLink: 'https://factorial.fr/termes-et-conditions-expenses-urssaf',
      termsModalTitle: i18n.t('expenses.settings.signed_invoices.terms.urssaf_modal_title'),
      termsModalDescription: i18n.t(
        'expenses.settings.signed_invoices.terms.urssaf_modal_description'
      ),
      documentsDescription: i18n.t(
        'expenses.settings.signed_invoices.urssaf.documents_description'
      ),
    },
    ade: {
      termsTitle: i18n.t('expenses.settings.signed_invoices.italy.terms_title'),
      termsDescription: i18n.t('expenses.settings.signed_invoices.italy.terms_description'),
      termsLink: 'https://factorial.it/termini-e-condizioni-expenses-ade',
      termsModalTitle: i18n.t('expenses.settings.signed_invoices.italy.terms_modal_title'),
      termsModalDescription: i18n.t(
        'expenses.settings.signed_invoices.italy.terms_modal_description'
      ),
      documentsDescription: i18n.t('expenses.settings.signed_invoices.italy.documents_description'),
    },
  }
  return copies[organizationId]
}

const homologatedCountries = Object.keys(SignableOrganizationPerCountry)
const emptyArray: number[] = []

const defaultOrganizationValues = {
  aeat: {
    applies: false,
    id: SignableOrganizationPerCountry.es,
    legalEntityIds: emptyArray.slice(),
  },
  urssaf: {
    applies: false,
    id: SignableOrganizationPerCountry.fr,
    legalEntityIds: emptyArray.slice(),
  },
  ade: {
    applies: false,
    id: SignableOrganizationPerCountry.it,
    legalEntityIds: emptyArray.slice(),
  },
}

export const getTaxAgencyOrgsByCountry = (
  legalEntityId: number,
  country: keyof typeof SignableOrganizationPerCountry
) => {
  const id = SignableOrganizationPerCountry[country] || null

  const organizations = defaultOrganizationValues

  if (id && organizations[id]) {
    organizations[id].applies = true
    !organizations[id].legalEntityIds.includes(legalEntityId) &&
      organizations[id].legalEntityIds.push(legalEntityId)
  }

  return organizations
}

export type TaxAgencyHomologations = Record<
  SignableOrganizationPerCountry,
  TaxAgencyHomologationOrg
>

export interface TaxAgencyHomologationOrg {
  applies: boolean
  id: SignableOrganizationPerCountry
  legalEntityIds: number[]
}

/**
 * Gets the organization that applies to a specific legal entity's country.
 * Returns null if the country doesn't have a signable organization or if the organization doesn't apply.
 */
export const getOrganizationForLegalEntity = (
  legalEntity: LegalEntity | null | undefined,
  organizations: TaxAgencyHomologations
): TaxAgencyHomologationOrg | null => {
  if (!legalEntity?.country) {
    return null
  }

  if (!isSignableCountry(legalEntity.country)) {
    return null
  }

  const orgId = SignableOrganizationPerCountry[legalEntity.country]
  const organization = organizations[orgId]

  return organization?.applies ? organization : null
}

const organizationIdNeeded = (
  hasFiles: boolean,
  hasSignedDocument: boolean,
  removingPreviousSignedDocument: boolean
): boolean => {
  if (!hasFiles) return false

  if (removingPreviousSignedDocument) {
    return true
  }

  if (hasSignedDocument) {
    return false
  }

  return true
}

const isSignableCountry = (
  country: string
): country is keyof typeof SignableOrganizationPerCountry => {
  return homologatedCountries.includes(country)
}

interface GetSignedForOrganizationParams {
  filesData: ExpensesFile[]
  expensable?: Expensable
  filesIdToRemove?: number[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
}
export const getSignedForOrganization = ({
  filesData,
  expensable,
  filesIdToRemove,
  taxAgencyOrganization,
}: GetSignedForOrganizationParams) => {
  const signedFileId = expensable?.expense?.signedDocument?.file?.id
  const isRemovingPreviousSignedDocument =
    signedFileId && filesIdToRemove ? filesIdToRemove.includes(signedFileId) : false

  const requiresOrganizationId = organizationIdNeeded(
    !!filesData?.length,
    !!expensable?.expense?.signedDocument?.id,
    isRemovingPreviousSignedDocument
  )
  const signedForOrganization = requiresOrganizationId ? taxAgencyOrganization : null
  return signedForOrganization
}

export const useTaxAgencyHomologations = (
  legalEntities: LegalEntity[]
): {
  loading: boolean
  enabled: boolean
  error: ApolloError | undefined
  organizations: TaxAgencyHomologations
} => {
  const { company } = useCurrentSession()
  const { loading, data, error } = useQuery(TaxAgencyHomologationHookDocument, {
    variables: {
      ids: [Features.expenses_homologation],
      companyId: company.id,
    },
  })
  const enabled =
    data?.companies?.featureAvailabilitiesConnection?.nodes.find(
      (feature) => feature.id === Features.expenses_homologation
    )?.available ?? false

  const organizations = defaultOrganizationValues

  if (!legalEntities || legalEntities.length === 0) {
    return {
      loading: false,
      enabled: false,
      error: undefined,
      organizations: organizations,
    }
  }

  const availableLegalEntities = legalEntities.filter((legalEntity) => {
    if (legalEntity.country === 'it' && !enabled) {
      return false
    }
    return homologatedCountries.includes(legalEntity.country)
  })

  availableLegalEntities.forEach((legalEntity) => {
    const country = legalEntity.country
    if (isSignableCountry(country)) {
      const id = SignableOrganizationPerCountry[country] || null

      if (id && organizations[id]) {
        organizations[id].applies = true
        !organizations[id].legalEntityIds.includes(legalEntity.id) &&
          organizations[id].legalEntityIds.push(legalEntity.id)
      }
    }
  })

  return {
    loading,
    enabled,
    organizations,
    error,
  }
}
